import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { isValidDomain } from '../../../../../models/UiUtils';
import {
	AccountAdminViewModel,
	AccountOperationsViewModel,
	AdminPermission,
	AdminUserSessionContext,
	ImpersonationContextViewModel,
} from '../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { TinyPopover } from '../../../../../web/components/TinyPopover';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { header } from '../../../../../web/styles/colors';
import { isEmailProviderConfigured } from '../../../../utils';
import { BusinessHours } from '../BusinessHours';
import { EmailConfiguration, IEmailConfigurationWithKey } from '../EmailConfiguration';
import { EmailDomainModal } from '../EmailDomainModal';
import { styleSheet } from './styles';

interface IEmailConfigurationWithDefaults extends IEmailConfigurationWithKey {
	defaultEmailSendLimits?: Api.IEmailSendLimit[];
	defaultSecondsBetweenSends?: number;
}

interface IProps extends IEventLoggingComponentProps, IErrorMessageComponentProps, IUserSessionComponentProps {
	account: Api.IAccount;
	accountOperations: AccountOperationsViewModel;
	className?: string;
	isLoading?: boolean;
	isOpen: boolean;
	onAllowMultipleDomains?(e: React.ChangeEvent<HTMLInputElement>): void;
	onClose(): void;
	onSave(configurations?: Api.IEmailProviderConfiguration[], forceMultipleDomains?: boolean): void;
	styles?: StyleDeclarationValue[];
}

interface IState {
	configurations: Record<string, IEmailConfigurationWithDefaults>;
	count: number;
	emailDomainInfoIsOpen?: boolean;
	emailDomainModalIsOpen?: boolean;
	forceMultipleDomains?: boolean;
	isBusinessHoursEnabled?: boolean;
	isBusinessHoursModalOpened?: boolean;
}

class _EmailConfigurations extends React.Component<IProps, IState> {
	private mAccountAdminVM: AccountAdminViewModel;

	public static calcState(props: IProps) {
		let count = 0;
		const configs: IEmailConfigurationWithDefaults[] = props.account
			? props.account.emailProviderConfigurations.map(c => {
					const configuration: IEmailConfigurationWithDefaults = {
						...c,
						emailProvider: c.emailProvider || 'Misc',
						exchange: {
							webServicesUrl: '',
							...c.exchange,
						},
						imap: {
							imapHost: '',
							imapPort: 993,
							imapUseSsl: true,
							smtpHost: '',
							smtpPort: 465,
							smtpUseSsl: true,
							...c.imap,
						},
						key: (++count).toString(),
					};

					return configuration;
				})
			: [];

		const configurations: Record<string, IEmailConfigurationWithDefaults> = {};
		for (const c of configs) {
			configurations[c.key] = c;
		}

		const forceMultipleDomains = props?.account?.emailDomain === '@none.com';

		return {
			configurations,
			count,
			forceMultipleDomains,
		};
	}

	constructor(props: IProps) {
		super(props);

		makeObservable<_EmailConfigurations, 'mAccountAdminVM'>(this, {
			mAccountAdminVM: observable,
		});

		this.mAccountAdminVM = new AccountAdminViewModel(props.userSession, props.account);
		this.state = {
			..._EmailConfigurations.calcState(props),
			isBusinessHoursEnabled: this.mAccountAdminVM?.features?.sendEmail?.observeSendIntervals,
		};
	}

	public UNSAFE_componentWillReceiveProps(props: IProps) {
		this.setState(_EmailConfigurations.calcState(props));
		this.initDefaults();
	}

	public render() {
		const { accountOperations, className, isLoading, isOpen, onClose, styles, userSession } = this.props;
		const { emailDomainInfoIsOpen, emailDomainModalIsOpen, isBusinessHoursEnabled, isBusinessHoursModalOpened } =
			this.state;

		const { configurations, forceMultipleDomains } = this.state;
		const keys = configurations ? Object.keys(configurations) : [];
		const onDelete = !!configurations && keys.length > 0 ? this.onDelete : undefined;

		const haveSupportLevel3 = (userSession as AdminUserSessionContext).hasPermission(AdminPermission.SupportLevel3);
		const configuredEmailDomain = accountOperations.account.features?.sendEmail?.domainName;

		return (
			<Modal
				className={`${this.props.className || ''}`}
				isOpen={isOpen}
				onRequestClose={onClose}
				useDefaultHeader={true}
			>
				<div className={css(styleSheet.root)}>
					<div className={css(styleSheet.checkboxButtonRow)}>
						<Checkbox
							id='account-detail-allow-multiple-domains'
							type='large'
							onChange={this.onDomainsCheckboxChanged}
							checked={forceMultipleDomains}
						>
							<div className={css(styleSheet.checkboxContent)}>Allow Multiple Domains</div>
						</Checkbox>
						<Button variant='secondary' size='sm' onClick={this.onAdd} styles={[styleSheet.addConfigurationButton]}>
							Add Configuration
						</Button>
					</div>
					<div className={`${css(styleSheet.container, ...(styles || []))} email-configurations ${className || ''}`}>
						{keys.map(key => (
							<EmailConfiguration
								accountOperations={accountOperations}
								configuration={{ ...configurations[key] }}
								key={key}
								onDelete={onDelete}
								onUpdate={this.onUpdate}
								userSession={userSession as AdminUserSessionContext}
							/>
						))}
					</div>
					<div className={css(styleSheet.twoColumnLayout)}>
						<div className={css(styleSheet.column)}>
							<div className={css(styleSheet.businessHoursHeader)}>Business Hours</div>
							{isBusinessHoursEnabled && (
								<div className={css(styleSheet.businessHoursManage)}>
									<button className={css(baseStyleSheet.brandLink)} onClick={this.toggleBusinessHoursModal}>
										Manage
									</button>
								</div>
							)}
							<Checkbox
								id='business-hours-observe-send-intervals'
								type='large'
								className={css(styleSheet.businessHoursCheckbox)}
								onChange={this.onUpdateEnableBusinessHours}
								checked={isBusinessHoursEnabled}
							>
								<div className={css(styleSheet.checkboxContent)}>Enable Business Hours</div>
							</Checkbox>
						</div>
						{haveSupportLevel3 && !accountOperations.account.features?.hipaa?.enabled && (
							<div className={css(styleSheet.column)}>
								<div className={css(styleSheet.emailDomainHeader)}>
									<span>Email Domain</span>
									<TinyPopover
										anchor={
											<figure
												className={css(styleSheet.infoIcon)}
												onClick={this.onToggleEmailDomainInfo}
												onMouseEnter={this.onOpenEmailDomainInfo}
												onKeyDown={this.onInfoIconKeyDown}
												role='button'
												aria-label='Information about Email Domain'
												tabIndex={0}
											>
												<InfoIcon fillColor={header} />
											</figure>
										}
										isOpen={emailDomainInfoIsOpen}
										dismissOnOutsideAction={true}
										onRequestClose={this.onCloseEmailDomainInfo}
										align='center'
										placement={['top', 'bottom', 'left', 'right']}
									>
										<p className={css(styleSheet.infoPopover)}>
											Configure a dedicated email domain for sending emails from Levitate&apos;s servers. This allows
											accounts with consumer email providers (Gmail, Yahoo, etc.) to send group emails.
										</p>
									</TinyPopover>
								</div>
								{configuredEmailDomain ? (
									<div className={css(styleSheet.emailDomainDomain)}>{configuredEmailDomain}</div>
								) : (
									<button className={css(baseStyleSheet.brandLink)} onClick={this.onOpenEmailDomainModal}>
										Request New Email Domain
									</button>
								)}
							</div>
						)}
					</div>
					<footer className={css(styleSheet.footer)}>
						<Button variant='primary' onClick={this.onSave}>
							Save
						</Button>
						<Button variant='secondary' onClick={this.onCancel}>
							Cancel
						</Button>
					</footer>
					{(isLoading || accountOperations.isGettingEmailSendLimits) && (
						<LoadingSpinner className={css(styleSheet.loading)} type='large' />
					)}
					<BusinessHours
						isOpen={isBusinessHoursModalOpened}
						isSaving={this.mAccountAdminVM.isUpdatingSendEmailFeatures}
						onRequestClose={this.onBusinessHoursRequestClose}
						sendEmailFeatures={this.mAccountAdminVM?.features?.sendEmail}
					/>
					{emailDomainModalIsOpen && (
						<EmailDomainModal
							modalProps={{
								isOpen: emailDomainModalIsOpen,
								onRequestClose: this.onCloseEmailDomainModal,
							}}
							features={accountOperations.account.features}
							onDomainRegistered={this.onDomainRegistered}
							impersonationContext={
								new ImpersonationContextViewModel({ account: { id: accountOperations.account.id } })
							}
						/>
					)}
				</div>
			</Modal>
		);
	}

	private getDefaultEmailSendLimits = (
		configuration: IEmailConfigurationWithDefaults,
		accountId: string
	): Promise<IEmailConfigurationWithDefaults> =>
		new Promise((resolve, reject) => {
			const { accountOperations } = this.props;
			accountOperations
				.getDefaultEmailSendLimits(configuration.emailProvider, configuration.domain, accountId)
				?.then(config => {
					resolve({
						...configuration,
						defaultEmailSendLimits: config.emailSendLimits,
						defaultSecondsBetweenSends: config.secondsBetweenSends,
					});
				})
				?.catch(reject);
		});

	private initDefaults = async () => {
		const { errorMessages } = this.props;
		const { configurations } = this.state;

		for (const configuration in configurations) {
			if (configurations[configuration]) {
				try {
					configurations[configuration] = await this.getDefaultEmailSendLimits(
						configurations[configuration],
						this.mAccountAdminVM.id
					);
					this.setState({ configurations });
				} catch (error) {
					errorMessages.pushApiError(error);
				}
			}
		}
	};

	private onDomainsCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (Object.keys(this.state.configurations).length > 1) {
			this.setState({ forceMultipleDomains: true });
		} else {
			this.setState({ forceMultipleDomains: e.target.checked });
		}
	};

	private onSave = () => {
		const configurations: Record<string, IEmailConfigurationWithDefaults> = JSON.parse(
			JSON.stringify(this.state.configurations)
		);

		const result: Api.IEmailProviderConfiguration[] = [];
		for (const key of Object.keys(configurations)) {
			const config = configurations[key];

			if (!isValidDomain(config.domain)) {
				toast.error('Domain required for every configuration');
				return;
			}

			switch (config.emailProvider) {
				case 'Yahoo':
				case 'Aol':
				case 'Imap': {
					if (!isEmailProviderConfigured(config)) {
						toast.error('Hosts and ports required for IMAP configurations');
						return;
					}
					config.exchange = undefined;
					break;
				}
				case 'Exchange': {
					if (!isEmailProviderConfigured(config)) {
						toast.error('Web Service Url required for Exchange connections');
						return;
					}
					const url = config.exchange?.webServicesUrl;

					if ((!!url && !/^(http|https)/.test(url)) || !/(\/ews\/Exchange\.asmx)$/i.test(url)) {
						toast.error('Web Service Url must begin with "http" or "https" and end with "/ews/Exchange.asmx"');
						return;
					}
					if (!!url && /(\/owa\/ews\/)/.test(url)) {
						config.exchange.webServicesUrl = url.replace('owa/ews', 'ews');

						toast.success('We have updated the Exchange details to not include owa');
					}
					config.imap = undefined;
					break;
				}
				default: {
					config.imap = undefined;
					config.exchange = undefined;
					break;
				}
			}
			result.push(config);
		}

		if (this.mAccountAdminVM?.features?.sendEmail?.observeSendIntervals !== this.state.isBusinessHoursEnabled) {
			this.onSaveSendEmailFeatures({
				observeSendIntervals: this.state.isBusinessHoursEnabled,
			}).then(() => this.props.onSave(result, this.state.forceMultipleDomains));
		} else {
			this.props.onSave(result, this.state.forceMultipleDomains);
		}
	};

	private onCancel = () => {
		this.props.onClose();
	};

	private onUpdate = async (configuration: IEmailConfigurationWithDefaults) => {
		const { errorMessages } = this.props;
		const { configurations } = this.state;

		if (
			!configuration.emailSendLimits?.length &&
			(!(configuration.key in configurations) ||
				configurations[configuration.key].emailProvider !== configuration.emailProvider)
		) {
			try {
				configurations[configuration.key] = await this.getDefaultEmailSendLimits(
					configuration,
					this.mAccountAdminVM.id
				);
				this.setState({ configurations });
			} catch (error) {
				errorMessages.pushApiError(error);
			}
		} else {
			configurations[configuration.key] = configuration;
			this.setState({ configurations });
		}
	};

	private onDelete = (configuration: IEmailConfigurationWithDefaults) => {
		const { configurations } = this.state;
		delete configurations[configuration.key];

		this.setState({
			configurations,
			forceMultipleDomains: Object.keys(configurations).length !== 1,
		});
	};

	private onAdd = () => {
		let { configurations, count } = this.state;
		const configuration = {
			exchange: {
				webServicesUrl: '',
			},
			imap: {
				imapHost: '',
				imapPort: 993,
				imapUseSsl: true,
				smtpHost: '',
				smtpPort: 465,
				smtpUseSsl: true,
			},
			key: (++count).toString(),
		};
		configurations[configuration.key] = configuration;

		this.setState({
			configurations,
			count,
			forceMultipleDomains: Object.keys(configurations).length !== 1,
		});
	};

	private onBusinessHoursRequestClose = (sendEmailFeatures?: Api.ISendEmailFeatures) => {
		if (sendEmailFeatures) {
			this.onSaveSendEmailFeatures(sendEmailFeatures).then(this.toggleBusinessHoursModal);
		} else {
			this.toggleBusinessHoursModal();
		}
	};

	private onSaveSendEmailFeatures = (sendEmailFeatures: Partial<Api.ISendEmailFeatures>) => {
		const { errorMessages, logApiError } = this.props;
		const toSave = this.mAccountAdminVM?.features?.sendEmail
			? { ...this.mAccountAdminVM.features.sendEmail, ...sendEmailFeatures }
			: sendEmailFeatures;
		const promise = this.mAccountAdminVM.updateSendEmailFeatures(toSave);
		if (promise) {
			promise.catch(e => {
				errorMessages.push(e);

				logApiError('Update-send-email-features', e);
			});
		}
		return promise;
	};

	private onUpdateEnableBusinessHours = (e: React.ChangeEvent<HTMLInputElement>) => {
		const isBusinessHoursEnabled = e.target.checked;
		this.setState({ isBusinessHoursEnabled });
	};

	private toggleBusinessHoursModal = () => {
		const { isBusinessHoursModalOpened } = this.state;
		this.setState({ isBusinessHoursModalOpened: !isBusinessHoursModalOpened });
	};

	private onOpenEmailDomainModal = () => {
		this.setState({ emailDomainModalIsOpen: true });
	};

	private onCloseEmailDomainModal = () => {
		this.setState({ emailDomainModalIsOpen: false });
	};

	private onDomainRegistered = () => {
		const { accountOperations, errorMessages } = this.props;

		accountOperations
			.load()
			?.then(() => {
				this.onCloseEmailDomainModal();
			})
			.catch(error => {
				errorMessages.pushApiError(error);
				this.onCloseEmailDomainModal();
			});
	};

	private onOpenEmailDomainInfo = () => {
		this.setState({ emailDomainInfoIsOpen: true });
	};

	private onToggleEmailDomainInfo = () => {
		this.setState(prevState => ({ emailDomainInfoIsOpen: !prevState.emailDomainInfoIsOpen }));
	};

	private onCloseEmailDomainInfo = () => {
		this.setState({ emailDomainInfoIsOpen: false });
	};

	private onInfoIconKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			this.onToggleEmailDomainInfo();
		}
	};
}

const EmailConfigurationsAsObserver = observer(_EmailConfigurations);
const EmailConfigurationsWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey
)(EmailConfigurationsAsObserver);
export const EmailConfigurations = withEventLogging(EmailConfigurationsWithContext, 'EmailConfigurations');
